import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../LanguageContext';
import { projLabel } from '../components/UnitsDeals/constants';
import { presetRange, PRESET_KEYS, PRESET_LABEL_KEY, ksaDate } from '../utils/dateRange';
import axios from 'axios';
import { LeadStory, STATUS_COLORS, fmtWhen } from '../components/LeadStory';

// ── Inline drill list: the leads behind a clicked summary chip ──
function DrillPanel({ drill, onClose, t }) {
  if (!drill) return null;
  const { title, color, loading, leads } = drill;
  return (
    <div style={{ marginTop: 10, border: `1px solid ${color}40`, borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: color + '12', borderBottom: `1px solid ${color}30` }}>
        <span style={{ fontWeight: 700, fontSize: '0.82rem', color }}>{title} {!loading && <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>({leads.length})</span>}</span>
        <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '1.1rem', lineHeight: 1 }}>&times;</button>
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{t('loading')}</div>
      ) : leads.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{t('noData')}</div>
      ) : (
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          {leads.map((l, i) => <DrillRow key={l.id || i} l={l} t={t} />)}
        </div>
      )}
    </div>
  );
}

// A drilled lead row that expands to its full story (details + comm history).
function DrillRow({ l, t }) {
  const [open, setOpen] = useState(false);
  const st = l.status || l.current_status;
  const sc = STATUS_COLORS[st] || '#888';
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', fontSize: '0.8rem', cursor: 'pointer', background: open ? 'var(--bg-secondary)' : 'transparent' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: sc, flexShrink: 0 }} />
        <span style={{ fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</span>
        <a href={`tel:${l.phone}`} onClick={e => e.stopPropagation()} style={{ direction: 'ltr', color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '0.76rem' }}>{l.phone}</a>
        <span style={{ padding: '1px 7px', borderRadius: 7, fontSize: '0.62rem', fontWeight: 700, background: sc + '18', color: sc, whiteSpace: 'nowrap' }}>{t('crmStatus_' + st)}</span>
        <span style={{ color: sc, fontSize: '0.72rem', flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && <LeadStory leadId={l.id} t={t} hideComms />}
    </div>
  );
}

// A pending lead: overdue follow-up / overdue visit / never-contacted, with the
// reason + the date it was due. Expands to the full lead story.
function PendingRow({ l, t, lang }) {
  const [open, setOpen] = useState(false);
  const sc = STATUS_COLORS[l.status] || '#888';
  const reason = l.pendingKind === 'visit'
    ? { label: lang === 'ar' ? 'زيارة متأخرة' : 'Overdue visit', date: l.siteVisitDate, color: '#ef4444' }
    : l.pendingKind === 'new'
      ? { label: lang === 'ar' ? 'لم يتم التواصل' : 'Never contacted', date: l.createdAt, color: '#dc2626' }
      : { label: lang === 'ar' ? 'متابعة متأخرة' : 'Overdue follow-up', date: l.followUpDate, color: '#f59e0b' };
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', fontSize: '0.8rem', cursor: 'pointer', background: open ? 'var(--bg-secondary)' : 'transparent' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: sc, flexShrink: 0 }} />
        <span style={{ fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</span>
        <span style={{ padding: '1px 7px', borderRadius: 7, fontSize: '0.6rem', fontWeight: 700, background: reason.color + '18', color: reason.color, whiteSpace: 'nowrap' }}>{reason.label}{reason.date ? ' · ' + fmtWhen(reason.date) : ''}</span>
        <a href={`tel:${l.phone}`} onClick={e => e.stopPropagation()} style={{ direction: 'ltr', color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '0.72rem' }}>{l.phone}</a>
        <span style={{ color: sc, fontSize: '0.72rem', flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && <LeadStory leadId={l.id} t={t} hideComms />}
    </div>
  );
}

// A compact clickable summary chip.
function Chip({ value, label, color, onClick, active }) {
  return (
    <div onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'baseline', gap: 5, padding: '6px 11px', borderRadius: 9,
      background: (color || '#888') + (active ? '28' : '12'), cursor: onClick ? 'pointer' : 'default',
      border: active ? `1px solid ${color}` : '1px solid transparent', whiteSpace: 'nowrap',
    }}>
      <b style={{ fontSize: '0.95rem', fontWeight: 800, color: color || 'var(--text-primary)' }}>{value}</b>
      <span style={{ fontSize: '0.66rem', color: 'var(--text-secondary)' }}>{label}{onClick ? ' ›' : ''}</span>
    </div>
  );
}

export default function MemberAnalytics() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const { name: rawName } = useParams();
  const name = decodeURIComponent(rawName || '');
  const [sp] = useSearchParams();
  const [period, setPeriod] = useState(sp.get('period') || 'today');
  const [customFrom, setCustomFrom] = useState(sp.get('from') || '');
  const [customTo, setCustomTo] = useState(sp.get('to') || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [statusLeads, setStatusLeads] = useState([]); // ALL leads with the picked status (created in period)

  const [s, setS] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true); // full-page spinner only on first load
  const [drill, setDrill] = useState(null);
  const [search, setSearch] = useState('');
  const [openLead, setOpenLead] = useState(null); // expanded lead group
  const [page, setPage] = useState(0);
  const [pending, setPending] = useState([]); // overdue/uncontacted leads for this rep

  // Pending leads = overdue follow-ups + overdue visits + never-contacted new
  // leads (past their due/agreed date). Period-independent ("overdue as of now"),
  // so always queried against today. Admins/managers/analytics users may pass
  // ?member; the endpoint ignores it for everyone else.
  useEffect(() => {
    axios.get('/api/leads/today-tasks', { params: { member: name, period: 'today' } })
      .then(r => {
        const d = r.data || {};
        const tag = (arr, kind) => (arr || []).map(l => ({ ...l, pendingKind: kind }));
        // A lead can qualify for more than one bucket (e.g. an overdue visit whose
        // follow-up is also overdue) — dedupe by id so it appears (and expands) once.
        const seen = new Set();
        const merged = [
          ...tag(d.overdueFollowUps, 'followup'),
          ...tag(d.overdueVisits, 'visit'),
          ...tag(d.backlogNew, 'new'),
        ].filter(l => { if (!l.id || seen.has(l.id)) return false; seen.add(l.id); return true; });
        setPending(merged);
      })
      .catch(() => setPending([]));
  }, [name]);

  const reqParams = useCallback(() => {
    const { from, to } = presetRange(period, customFrom, customTo);
    return (from && to) ? { date_from: from, date_to: to } : { period };
  }, [period, customFrom, customTo]);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const [an, act] = await Promise.all([
          axios.get('/api/leads/analytics', { params: reqParams() }),
          axios.get(`/api/leads/member-activity/${encodeURIComponent(name)}`, { params: { ...reqParams(), limit: 200 } }),
        ]);
        setS((an.data?.salesPerformance || []).find(x => x.name === name) || null);
        setActivity(act.data?.activity || []);
      } catch { setS(null); }
      setLoading(false);
      setFirstLoad(false);
    })();
  }, [name, reqParams]);

  // Reset to the first page whenever a filter changes.
  useEffect(() => { setPage(0); }, [search, name, period, statusFilter, projectFilter]);
  // A new period reloads data; clear status/project so we never show an empty set.
  useEffect(() => { setStatusFilter('all'); setProjectFilter('all'); }, [period]);

  // When a status is picked, load ALL the rep's leads with that status (created
  // in the period) — the full population, not just the ones worked in the feed.
  useEffect(() => {
    if (statusFilter === 'all') { setStatusLeads([]); return; }
    let alive = true;
    axios.get(`/api/leads/member-segment/${encodeURIComponent(name)}`, { params: { ...reqParams(), segment: 'cur_status', status: statusFilter } })
      .then(r => { if (alive) setStatusLeads(r.data?.leads || []); })
      .catch(() => { if (alive) setStatusLeads([]); });
    return () => { alive = false; };
  }, [name, statusFilter, reqParams]);

  const fetchSegment = useCallback(async (key) => {
    try {
      const r = await axios.get(`/api/leads/member-segment/${encodeURIComponent(name)}`, { params: { ...reqParams(), segment: key } });
      return r.data?.leads || [];
    } catch { return []; }
  }, [name, reqParams]);

  const openDrill = useCallback(async (key, title, color) => {
    if (drill && drill.key === key) { setDrill(null); return; }
    setDrill({ key, title, color, loading: true, leads: [] });
    const leads = await fetchSegment(key);
    setDrill(d => (d && d.key === key) ? { ...d, loading: false, leads } : d);
  }, [drill, fetchSegment]);

  if (loading && firstLoad) return <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-secondary)' }}>{t('loading')}</div>;
  if (!s) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/analytics')} style={{ marginBottom: 16 }}>&larr; {t('anDailyPerformance')}</button>
        <div style={{ color: 'var(--text-secondary)' }}>{t('noData')} — {name}</div>
      </div>
    );
  }

  const ps = s.periodStats || { total: 0, new: 0, followup: 0, visits: 0, won: 0, lost: 0 };
  const pct = s.todayCompletionRate || 0;
  const barColor = pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : pct > 0 ? '#ef4444' : '#888';
  const avg = Number(s.effort?.avgTouches) || 0;

  const commTypeMeta = (ct) => {
    if (ct === 'directive') return { icon: '🚩', color: '#ef4444', label: lang === 'ar' ? 'توجيه' : 'Directive' };
    if (ct === 'directive_reply') return { icon: '↩', color: '#16a34a', label: lang === 'ar' ? 'رد توجيه' : 'Reply' };
    if (ct === 'note') return { icon: '📝', color: '#8b5cf6', label: t('crmCommType_note') || 'Note' };
    if (ct === 'call') return { icon: '📞', color: '#3b82f6', label: t('crmCommType_call') || 'Call' };
    if (ct === 'whatsapp') return { icon: '💬', color: '#22c55e', label: t('crmCommType_whatsapp') || 'WhatsApp' };
    return { icon: '💬', color: '#3b82f6', label: t('crmCommType_' + ct) || ct };
  };

  // Group the rep's activity by lead; most-recently-touched lead first.
  const q = search.trim().toLowerCase();
  const groupsMap = new Map();
  for (const it of activity) {
    if (!groupsMap.has(it.leadId)) groupsMap.set(it.leadId, { leadId: it.leadId, leadName: it.leadName, leadPhone: it.leadPhone, leadStatus: it.leadStatus, leadProject: it.leadProject || '', items: [] });
    groupsMap.get(it.leadId).items.push(it);
  }
  let groups = [...groupsMap.values()];
  // Sort each lead's entries newest-first, then order leads by their most-recent
  // entry — so the list stays date-ordered regardless of the API's row order.
  groups.forEach(g => {
    g.items.sort((a, b) => new Date(b.at) - new Date(a.at));
    g.latest = g.items[0];
    g.last = g.latest?.at;
  });
  groups.sort((a, b) => new Date(b.last) - new Date(a.last));

  // In-page filters: status, project, and free-text search.
  const statusOptions = [...new Set(groups.map(g => g.leadStatus).filter(Boolean))];
  const projectOptions = [...new Set(groups.flatMap(g => (g.leadProject || '').split(',').map(s => s.trim()).filter(Boolean)))];
  if (statusFilter !== 'all') {
    // Status view: show ALL the rep's leads with this status (created in period),
    // enriched with any activity — the full population behind the per-status
    // count (not just the ones worked in the feed). Reconciles with the card.
    const itemsByLead = {};
    for (const it of activity) { (itemsByLead[it.leadId] = itemsByLead[it.leadId] || []).push(it); }
    groups = statusLeads.map(l => {
      const items = (itemsByLead[l.id] || []).slice().sort((a, b) => new Date(b.at) - new Date(a.at));
      return { leadId: l.id, leadName: l.name, leadPhone: l.phone, leadStatus: l.status, leadProject: l.project || '', items, latest: items[0] || null, last: items[0]?.at || l.updated_at };
    });
    if (q) groups = groups.filter(g => (g.leadName || '').toLowerCase().includes(q) || (g.leadPhone || '').includes(search.trim()) || g.items.some(it => ((it.message || '') + ' ' + (it.note || '')).toLowerCase().includes(q)));
    groups.sort((a, b) => new Date(b.last) - new Date(a.last));
  } else {
    if (projectFilter !== 'all') groups = groups.filter(g => (g.leadProject || '').split(',').map(s => s.trim()).includes(projectFilter));
    if (q) groups = groups.filter(g => (g.leadName || '').toLowerCase().includes(q) || g.items.some(it => ((it.message || '') + ' ' + (it.note || '')).toLowerCase().includes(q)));
  }

  // Dedupe by leadId — cards share the `openLead` state, so a repeated leadId
  // would render two cards that expand together (a doubled comm log / story).
  const seenLead = new Set();
  groups = groups.filter(g => { if (seenLead.has(g.leadId)) return false; seenLead.add(g.leadId); return true; });

  const PAGE_SIZE = 15;
  const totalPages = Math.max(1, Math.ceil(groups.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages - 1);
  const pageGroups = groups.slice(curPage * PAGE_SIZE, (curPage + 1) * PAGE_SIZE);

  const previewText = (it) => it.kind === 'comment'
    ? it.message
    : (it.fromStatus === it.toStatus
      ? `${t('crmStatus_' + it.toStatus)}${it.note ? ' — ' + it.note : ''}`
      : `${t('crmStatus_' + it.fromStatus)} → ${t('crmStatus_' + it.toStatus)}${it.note ? ' — ' + it.note : ''}`);

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '8px 4px 60px' }}>
      <button className="btn btn-secondary btn-sm" onClick={() => navigate('/analytics')} style={{ marginBottom: 14 }}>&larr; {t('anDailyPerformance')}</button>

      {/* Header + compact summary strip */}
      <div className="card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 14 }}>
          <div>
            <h2 style={{ fontSize: '1.3rem', fontWeight: 800, margin: 0 }}>{s.name}</h2>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 3 }}>{s.total} {t('crmTotalLeads')}</div>
          </div>
          <div style={{ width: 54, height: 54, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: barColor + '12', border: `2.5px solid ${barColor}`, fontWeight: 800, fontSize: '0.95rem', color: barColor, flexShrink: 0 }}>{pct.toFixed(0)}%</div>
        </div>
        {/* Target · Done · Remaining — the rep's full actionable workload for the period */}
        {(() => {
          const target = s.periodTarget || 0;
          const done = s.todayWorked || 0;
          const remaining = Math.max(0, target - done);
          const bl = s.backlog || { notContacted: 0, overdueFollowups: 0 };
          const periodTasks = s.todayTotalTasks || 0;
          const box = (label, val, color) => (
            <div style={{ flex: '1 1 84px', minWidth: 84, textAlign: 'center', padding: '9px 6px', background: 'var(--bg-secondary)', borderRadius: 10, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: '1.35rem', fontWeight: 800, color: color || 'var(--text-primary)' }}>{val}</div>
              <div style={{ fontSize: '0.66rem', color: 'var(--text-secondary)', marginTop: 2 }}>{label}</div>
            </div>
          );
          return (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {box(lang === 'ar' ? 'الهدف' : 'Target', target, 'var(--accent)')}
                {box(lang === 'ar' ? 'أُنجز' : 'Done', done, '#22c55e')}
                {box(lang === 'ar' ? 'المتبقي' : 'Remaining', remaining, remaining > 0 ? '#f59e0b' : '#22c55e')}
              </div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5, textAlign: lang === 'ar' ? 'right' : 'left' }}>
                {lang === 'ar'
                  ? `الهدف = مهام الفترة (جدد + متابعات مستحقة) ${periodTasks} + غير متواصل معهم ${bl.notContacted} + متابعات فائتة ${bl.overdueFollowups}`
                  : `Target = period tasks (new + due follow-ups) ${periodTasks} + never-contacted ${bl.notContacted} + overdue follow-ups ${bl.overdueFollowups}`}
              </div>
            </div>
          );
        })()}
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip value={ps.new} label={lang === 'ar' ? 'جديد' : 'New'} color="#8b5cf6" active={drill?.key === 'new'} onClick={ps.new > 0 ? () => openDrill('new', lang === 'ar' ? 'عملاء جدد' : 'New', '#8b5cf6') : undefined} />
          <Chip value={ps.followup} label={lang === 'ar' ? 'متابعات' : 'Follow-up'} color="#f59e0b" active={drill?.key === 'followup'} onClick={ps.followup > 0 ? () => openDrill('followup', lang === 'ar' ? 'متابعات' : 'Follow-up', '#f59e0b') : undefined} />
          <Chip value={ps.visits} label={lang === 'ar' ? 'زيارات' : 'Visits'} color="#06b6d4" active={drill?.key === 'visits'} onClick={ps.visits > 0 ? () => openDrill('visits', lang === 'ar' ? 'زيارات' : 'Visits', '#06b6d4') : undefined} />
          <Chip value={ps.won} label={lang === 'ar' ? 'صفقات' : 'Won'} color="#22c55e" active={drill?.key === 'won'} onClick={ps.won > 0 ? () => openDrill('won', lang === 'ar' ? 'صفقات' : 'Won', '#22c55e') : undefined} />
          <Chip value={ps.lost} label={lang === 'ar' ? 'خسارة' : 'Lost'} color="#ef4444" active={drill?.key === 'lost'} onClick={ps.lost > 0 ? () => openDrill('lost', lang === 'ar' ? 'خسارة' : 'Lost', '#ef4444') : undefined} />
          <Chip value={avg.toFixed(1) + '×'} label={t('anEffortAvgTouches')} color={avg >= 3 ? '#22c55e' : avg >= 2 ? '#f59e0b' : '#ef4444'} />
        </div>
        {/* Pipeline — same per-status counts as the team card (leads created in
            the period), drillable to the exact leads so both pages reconcile. */}
        {(() => {
          const pipe = [
            { v: s.contacted || 0, key: 'ps_contacted', label: t('crmStatus_contacted'), color: '#3b82f6' },
            { v: s.interested || 0, key: 'ps_interested', label: t('crmStatus_interested'), color: '#8b5cf6' },
            { v: (s.visitScheduled || 0) + (s.visitDone || 0), key: 'ps_visit', label: t('crmStatus_site_visit_scheduled'), color: '#06b6d4' },
            { v: s.offerSent || 0, key: 'ps_offer_sent', label: t('crmStatus_offer_sent'), color: '#0ea5e9' },
            { v: s.negotiating || 0, key: 'ps_negotiating', label: t('crmStatus_negotiating'), color: '#ec4899' },
            { v: s.won || 0, key: 'ps_won', label: t('crmStatus_won'), color: '#22c55e' },
          ].filter(p => p.v > 0);
          if (!pipe.length) return null;
          return (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 2 }}>{t('anPipeline')}</div>
              <div style={{ fontSize: '0.62rem', color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.4 }}>
                {lang === 'ar'
                  ? 'عدد العملاء المُستلَمين في الفترة المحددة حسب حالتهم الحالية — اضغط أي رقم لعرض قائمة عملائه'
                  : 'Leads received in the selected period, grouped by their current status — tap any number to see its list'}
              </div>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
                {pipe.map((p, i) => (
                  <Chip key={i} value={p.v} label={p.label} color={p.color} active={drill?.key === p.key} onClick={() => openDrill(p.key, p.label, p.color)} />
                ))}
              </div>
            </div>
          );
        })()}
        <DrillPanel drill={drill} onClose={() => setDrill(null)} t={t} />
      </div>

      {/* Pending leads — overdue follow-ups + overdue visits + never-contacted */}
      {pending.length > 0 && (() => {
        const pc = { followup: 0, visit: 0, new: 0 };
        pending.forEach(l => { pc[l.pendingKind] = (pc[l.pendingKind] || 0) + 1; });
        const breakdown = [
          { v: pc.followup, label: lang === 'ar' ? 'متابعة متأخرة' : 'Overdue follow-up', color: '#f59e0b' },
          { v: pc.visit, label: lang === 'ar' ? 'زيارة متأخرة' : 'Overdue visit', color: '#ef4444' },
          { v: pc.new, label: lang === 'ar' ? 'لم يتم التواصل' : 'Never contacted', color: '#dc2626' },
        ].filter(b => b.v > 0);
        return (
        <div className="card" style={{ padding: 18, marginBottom: 16, border: '1px solid rgba(239,68,68,0.35)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1rem' }}>⚠</span>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#ef4444' }}>{lang === 'ar' ? 'العملاء المعلّقون' : 'Pending leads'}</h3>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#fff', background: '#ef4444', borderRadius: 10, padding: '1px 9px' }}>{pending.length}</span>
            <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>{lang === 'ar' ? 'تجاوزت الموعد المحدد ولم تُنجز' : 'past their due date, not completed'}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {breakdown.map((b, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, padding: '3px 9px', borderRadius: 8, background: b.color + '14' }}>
                <b style={{ fontSize: '0.85rem', color: b.color }}>{b.v}</b>
                <span style={{ fontSize: '0.62rem', color: 'var(--text-secondary)' }}>{b.label}</span>
              </span>
            ))}
          </div>
          <div style={{ maxHeight: 440, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
            {pending.map((l, i) => <PendingRow key={l.id || i} l={l} t={t} lang={lang} />)}
          </div>
        </div>
        );
      })()}

      {/* Activity grouped by lead — most recently touched first */}
      <div className="card" style={{ padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>
            {lang === 'ar' ? 'العملاء والنشاط' : 'Leads & Activity'}
            <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-secondary)', marginInlineStart: 6 }}>({groups.length})</span>
          </h3>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={lang === 'ar' ? 'بحث بالاسم أو النص…' : 'Search name or text…'}
            style={{ padding: '7px 11px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.8rem', minWidth: 180 }} />
        </div>

        {/* Filters: period · status · project */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {PRESET_KEYS.map(p => (
              <button key={p} onClick={() => setPeriod(p)} style={{
                padding: '5px 11px', borderRadius: 7, border: '1px solid var(--border)', cursor: 'pointer', fontSize: '0.74rem', fontWeight: 600,
                background: period === p ? 'var(--accent)' : 'var(--bg-primary)', color: period === p ? '#fff' : 'var(--text-secondary)',
              }}>{t(PRESET_LABEL_KEY[p])}</button>
            ))}
          </div>
          {period === 'custom' && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" value={customFrom} max={customTo || ksaDate(0)} onChange={e => setCustomFrom(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.74rem' }} />
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.74rem' }}>{t('to')}</span>
              <input type="date" value={customTo} min={customFrom || undefined} max={ksaDate(0)} onChange={e => setCustomTo(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.74rem' }} />
            </div>
          )}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.76rem', cursor: 'pointer' }}>
            <option value="all">{lang === 'ar' ? 'كل الحالات' : 'All statuses'}</option>
            {statusOptions.map(st => <option key={st} value={st}>{t('crmStatus_' + st)}</option>)}
          </select>
          {projectOptions.length > 0 && (
            <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.76rem', cursor: 'pointer' }}>
              <option value="all">{lang === 'ar' ? 'كل المشاريع' : 'All projects'}</option>
              {projectOptions.map(pr => <option key={pr} value={pr}>{projLabel(pr, t)}</option>)}
            </select>
          )}
        </div>

        {groups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{t('noData')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pageGroups.map(g => {
              const sc = STATUS_COLORS[g.leadStatus] || '#888';
              const isOpen = openLead === g.leadId;
              const meta = g.latest ? (g.latest.kind === 'comment' ? commTypeMeta(g.latest.commType) : { icon: '🔄' }) : { icon: '👤' };
              return (
                <div key={g.leadId} style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', borderInlineStart: `3px solid ${sc}` }}>
                  <div onClick={() => setOpenLead(isOpen ? null : g.leadId)} style={{ padding: '11px 13px', cursor: 'pointer', background: isOpen ? 'var(--bg-secondary)' : 'transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: sc, flexShrink: 0 }} />
                      <b style={{ fontSize: '0.9rem' }}>{g.leadName}</b>
                      <span style={{ padding: '1px 7px', borderRadius: 7, fontSize: '0.62rem', fontWeight: 700, background: sc + '18', color: sc }}>{t('crmStatus_' + g.leadStatus)}</span>
                      {g.leadProject && (
                        <span title={lang === 'ar' ? 'المشروع' : 'Project'} style={{ padding: '1px 7px', borderRadius: 7, fontSize: '0.62rem', fontWeight: 700, background: 'var(--bg-secondary)', color: 'var(--accent)', border: '1px solid var(--border)' }}>
                          🏢 {projLabel(g.leadProject, t)}
                        </span>
                      )}
                      <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>· {g.items.length} {lang === 'ar' ? 'نشاط' : 'entries'} · {fmtWhen(g.last)}</span>
                      <a href={`tel:${g.leadPhone}`} onClick={e => e.stopPropagation()} style={{ direction: 'ltr', marginInlineStart: 'auto', fontSize: '0.72rem', color: 'var(--text-secondary)', textDecoration: 'none' }}>{g.leadPhone}</a>
                      <span style={{ color: sc, fontSize: '0.72rem', flexShrink: 0 }}>{isOpen ? '▲' : '▼'}</span>
                    </div>
                    {!isOpen && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: '0.76rem', color: 'var(--text-secondary)' }}>
                        <span style={{ flexShrink: 0 }}>{meta.icon}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.latest ? (previewText(g.latest) || '—') : (lang === 'ar' ? 'لا نشاط في الفترة' : 'No activity in period')}</span>
                      </div>
                    )}
                  </div>
                  {isOpen && <LeadStory leadId={g.leadId} t={t} hideComms />}
                </div>
              );
            })}

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 10 }}>
                <button onClick={() => { setOpenLead(null); setPage(p => Math.max(0, p - 1)); }} disabled={curPage === 0}
                  style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-card)', color: curPage === 0 ? 'var(--border)' : 'var(--text-primary)', cursor: curPage === 0 ? 'default' : 'pointer', fontSize: '0.78rem', fontWeight: 700 }}>
                  {lang === 'ar' ? '‹ السابق' : '‹ Prev'}
                </button>
                <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', padding: '0 8px' }}>
                  {lang === 'ar' ? `صفحة ${curPage + 1} من ${totalPages}` : `Page ${curPage + 1} of ${totalPages}`}
                </span>
                <button onClick={() => { setOpenLead(null); setPage(p => Math.min(totalPages - 1, p + 1)); }} disabled={curPage >= totalPages - 1}
                  style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-card)', color: curPage >= totalPages - 1 ? 'var(--border)' : 'var(--text-primary)', cursor: curPage >= totalPages - 1 ? 'default' : 'pointer', fontSize: '0.78rem', fontWeight: 700 }}>
                  {lang === 'ar' ? 'التالي ›' : 'Next ›'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
